import { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, X, Send, Sparkles, ChevronDown, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ReactMarkdown from 'react-markdown';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const QUICK_ACTIONS = [
  { label: '🧭 Browse All Tools', message: 'What tools are available?' },
  { label: '💻 Open Code Studio', message: 'Take me to Code Studio' },
  { label: '📄 Merge PDFs', message: 'How do I merge PDFs?' },
  { label: '🤖 Help me', message: 'What can you help me with on this page?' },
];

const getPageContext = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length <= 1) return 'the Dashboard home page';
  if (segments[1] === 'tools' && segments[2]) {
    return `the ${segments[2].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} tool page`;
  }
  if (segments[1] === 'my-sites') return 'the My Sites page';
  if (segments[1] === 'settings') return 'the Settings page';
  if (segments[1] === 'profile') return 'the Profile page';
  if (segments[1] === 'guide') return 'the Guide page';
  if (segments[1] === 'catalogue') return 'the Catalogue page';
  if (segments[1] === 'code-studio') return 'the Code Studio IDE';
  return `the ${segments.slice(1).join(' / ')} page`;
};

const FloatingChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [showPulse, setShowPulse] = useState(true);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Parse [BUTTON:label:path] from AI responses
  const renderContentWithButtons = (content: string) => {
    const buttonRegex = /\[BUTTON:(.*?):(\/[^\]]*)\]/g;
    const parts: (string | { label: string; path: string })[] = [];
    let lastIndex = 0;
    let match;

    while ((match = buttonRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }
      parts.push({ label: match[1], path: match[2] });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    // Separate text parts and button parts
    const textContent = parts.filter((p): p is string => typeof p === 'string').join('');
    const buttons = parts.filter((p): p is { label: string; path: string } => typeof p !== 'string');

    return (
      <>
        {textContent.trim() && (
          <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none [&_p]:m-0 [&_pre]:my-1.5 [&_code]:text-[11px] [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0">
            <ReactMarkdown>{textContent.trim()}</ReactMarkdown>
          </div>
        )}
        {buttons.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {buttons.map((btn, i) => (
              <button
                key={i}
                onClick={() => { navigate(btn.path); setIsOpen(false); }}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg bg-primary/15 text-primary hover:bg-primary/25 border border-primary/20 transition-colors"
              >
                {btn.label}
                <span className="text-[10px]">→</span>
              </button>
            ))}
          </div>
        )}
      </>
    );
  };

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Hide pulse after first open
  useEffect(() => {
    if (isOpen) setShowPulse(false);
  }, [isOpen]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return;

    const pageContext = getPageContext(location.pathname);
    const userMsg: ChatMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    let assistantContent = '';

    try {
      // Build history with page context injected into latest user message
      const contextualMessage = `[Context: The user is currently on ${pageContext} at route ${location.pathname}]\n\n${text}`;
      const history = [
        ...messages.map(m => ({ role: m.role, content: m.content })),
        { role: 'user' as const, content: contextualMessage },
      ];

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tam-ai-agent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ messages: history }),
          signal: controller.signal,
        }
      );

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'AI service error' }));
        throw new Error(err.error || `Error ${resp.status}`);
      }

      if (!resp.body) throw new Error('No response body');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantContent += content;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) =>
                    i === prev.length - 1 ? { ...m, content: assistantContent } : m
                  );
                }
                return [...prev, { role: 'assistant', content: assistantContent }];
              });
            }
          } catch {
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: `❌ ${e.message || 'Something went wrong. Try again.'}` },
        ]);
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [isStreaming, messages, location.pathname]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Chat Panel */}
      <div
        className={cn(
          'fixed bottom-20 right-4 z-[9999] w-[360px] max-w-[calc(100vw-2rem)] transition-all duration-300 ease-out',
          isOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto scale-100'
            : 'opacity-0 translate-y-4 pointer-events-none scale-95'
        )}
      >
        <div className="rounded-2xl border border-border/60 bg-card shadow-2xl shadow-primary/10 overflow-hidden flex flex-col" style={{ height: '500px', maxHeight: 'calc(100vh - 8rem)' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary to-accent border-b border-border/30">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center backdrop-blur-sm">
                <Bot className="w-4.5 h-4.5 text-primary-foreground" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-primary-foreground">TAM AI</h3>
                <p className="text-[11px] text-primary-foreground/70 leading-none">I'm here to help</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                  onClick={() => setMessages([])}
                  title="Clear chat"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                onClick={() => setIsOpen(false)}
              >
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">Hey there! 👋</p>
                <p className="text-xs text-muted-foreground mb-4">
                  I can help with any tool, answer questions, or write code. Try a quick action below!
                </p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {QUICK_ACTIONS.map((action) => (
                    <button
                      key={action.label}
                      onClick={() => sendMessage(action.message)}
                      className="text-[11px] px-2.5 py-1.5 rounded-full border border-border bg-secondary/50 text-foreground hover:bg-primary/10 hover:border-primary/30 transition-colors"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={cn(
                  'flex',
                  msg.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                <div
                  className={cn(
                    'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed',
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-secondary/70 text-foreground rounded-bl-md border border-border/40'
                  )}
                >
                  {msg.role === 'assistant' ? (
                    renderContentWithButtons(msg.content)
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}

            {isStreaming && messages[messages.length - 1]?.role !== 'assistant' && (
              <div className="flex justify-start">
                <div className="bg-secondary/70 rounded-2xl rounded-bl-md px-4 py-3 border border-border/40">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border/40 p-3 bg-card">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask TAM AI anything..."
                rows={1}
                className="flex-1 resize-none rounded-xl border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 max-h-24"
                style={{ minHeight: '40px' }}
              />
              <Button
                size="icon"
                className="h-10 w-10 rounded-xl shrink-0"
                disabled={!input.trim() || isStreaming}
                onClick={() => sendMessage(input)}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'fixed bottom-4 right-4 z-[9999] w-14 h-14 rounded-full shadow-lg transition-all duration-200',
          'bg-gradient-to-br from-primary to-accent text-primary-foreground',
          'hover:shadow-xl hover:shadow-primary/25 active:scale-95',
          'flex items-center justify-center'
        )}
        aria-label={isOpen ? 'Close TAM AI' : 'Open TAM AI'}
      >
        {isOpen ? (
          <X className="w-5 h-5 transition-transform duration-200" />
        ) : (
          <>
            <Bot className="w-6 h-6" />
            {showPulse && (
              <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping pointer-events-none" />
            )}
          </>
        )}
      </button>
    </>
  );
};

export default FloatingChatWidget;
